
export const BRUSH_RADIUS = 10;

export const landThresholds = {
    sea: 0.0,
    highland: 2.0,
    mountain: 5.0,
    snow: 7.0,
    /** Width of the sandy shoreline band below sea level. Heights in [sea - sandBand, sea) render as sand. */
    sandBand: 0.3,
};

export const SOCIAL = {
    /** Squared radius (world units²) within which a being senses others for
     *  attraction and the Socializing trigger. 64 = 8-unit radius — wide enough
     *  that dispersed beings drift back together rather than needing to already touch. */
    SENSE_RADIUS_SQ: 64,
    /** Squared radius (world units²) within which a nearby being satisfies the
     *  social need, so attraction fades to zero — beings cluster loosely rather
     *  than packing tight. The pull ramps linearly from 0 at this radius up to
     *  full strength at `SENSE_RADIUS_SQ` (the edge of vision). 16 = 4-unit radius. */
    COMFORT_RADIUS_SQ: 16,
    /** Strength of the pull toward the centroid of nearby beings while exploring.
     *  Applied as a cosine-weighted term ([-1, 1] × weight) per candidate direction,
     *  so it nudges among comparable-terrain directions without overriding danger
     *  avoidance. Only Level 2+ passes this; in Level 1 the weight is 0. */
    ATTRACTION_WEIGHT: 1.2,
};

export const LEVEL1 = {
    /** Number of simulation ticks between tip messages shown to the player. */
    TIP_INTERVAL_TICKS: 40,
    /** Tuning for the Level 1 social-cohesion win mechanic. */
    SOCIAL_COHESION: {
        /** Score that must be reached for the level to be considered complete. */
        TARGET: 1000,
        /** Radius squared (grid units²) within which another being counts as "nearby". */
        NEARBY_RADIUS_SQ: 25,
        /** Probability per isolated being per tick that their cohesion score decays by 1. */
        DECAY_PROBABILITY: 0.5,
    },
};

export const LEVEL2 = {
    /** Number of beast kills required to complete the level. */
    BEASTS_TO_KILL: 3,
    /** Beasts scattered across the map when the level begins. Only one more than
     *  `BEASTS_TO_KILL`, so losing beasts to bad terrain has a real cost — the
     *  Spawn Beast tool is the recovery valve once the population reaches zero. */
    BEAST_COUNT: 4,
};

export const BEAST = {
    /** Health lost per tick on safe terrain — heavier than beings (larger body). */
    HEALTH_DECAY_BASE: 0.03,
    /** Health lost per tick in water or on mountains. */
    HEALTH_DECAY_DANGER: 0.08,
    /** Health gained per tick while eating on low land. Slower than beings. */
    FOOD_RATE: 0.08,
    /** Health fraction below which a Beast starts seeking food. */
    HUNGER_THRESHOLD: 0.65,
    /** Health fraction above which a Beast stops eating and sleeps. */
    FULL_THRESHOLD: 0.95,
    /** Ticks a Beast sleeps after eating to full. */
    SLEEP_TICKS: 3,
    /** Fraction of a full grid step the Beast moves per tick when exploring. */
    EXPLORE_SPEED: 0.5,
    /** Ticks a Beast holds a direction before re-evaluating while exploring. */
    DIRECTION_COMMIT_TICKS: 4,
    /** Squared radius (world units²) within which a Being senses a Beast — used for
     *  hunting and danger reactions. 225 = 15-unit detection radius. */
    DETECTION_RADIUS_SQ: 225,
    /** Fraction of a full grid step a Beast moves per tick while fleeing. Greater
     *  than the Beings' pursuit step (a full 1.0 in `HuntingState`) so a fleeing
     *  Beast genuinely outruns its hunters and contact time is finite. */
    FLEE_SPEED: 1.25,
    /** Social-attraction weight used while fleeing — the negative mirror of
     *  `SOCIAL.ATTRACTION_WEIGHT`. Passed to `bestDirection` so a fleeing Beast is
     *  repelled from the centroid of nearby beings, steering its escape away from
     *  the herd rather than toward it. */
    FLEE_AVOIDANCE_WEIGHT: -1.2,
    /** Squared radius (world units²) within which an angered Beast gores a Being to
     *  death. 4 = a 2-unit reach, far tighter than `HuntingState.HUNT_RANGE_SQ` (6
     *  units), so a pack can throw from a safe standoff but pays for closing all the
     *  way in. A Beast that can still run stays out of this range on its own; it only
     *  becomes a threat once the player has cornered it, which is exactly when the
     *  hunt is winnable. */
    GORE_RADIUS_SQ: 4,
    /** Ticks after the last blow from a hunting Being during which the Beast's death
     *  still counts as a hunt kill, whatever ultimately lands it. At 500ms/tick,
     *  10 = 5 seconds — long enough that a pack that wounds a Beast and drives it
     *  into the sea is credited, short enough that a Beast which shakes off a hunt,
     *  wanders for a while and then drowns is not. */
    KILL_CREDIT_TICKS: 10,
};

export const AI = {
    SIGHT_DEPTH: 5,
    /** Ticks a Being commits to a direction before re-evaluating. At 500ms/tick,
     *  2 = ~1 second — fast enough that terrain reshaping visibly redirects Beings. */
    DIRECTION_COMMIT_TICKS: 2,
    /** Ticks a Being rests after eating to full. Kept short so Beings stay mobile. */
    REST_TICKS: 2,
    /** Health fraction below which a Being starts seeking food. Higher values mean
     *  Beings seek food sooner, keeping them engaged with the terrain. */
    HUNGER_THRESHOLD: 0.3,
    FULL_THRESHOLD: 0.95,
    // Distance (in grid cells) from the nearest edge below which the inland
    // bias kicks in. Above this, beings ignore the bias so they don't all
    // pile up in the centre.
    INLAND_BIAS_THRESHOLD: 3,
    /** Health lost per tick on safe land. At 0.04 and low-land food of 0.15/tick,
     *  Beings net +0.11 on good ground, break even on highland, and rapidly die
     *  in danger. This creates visible food pressure without constant drowning. */
    HEALTH_DECAY_BASE: 0.04,
    /** Health lost per tick on mountain terrain (above mountain threshold). */
    HEALTH_DECAY_MOUNTAIN: 0.06,
    /** Health lost per tick when underwater (below sea threshold). */
    HEALTH_DECAY_WATER: 0.10,
    /** Health gained per tick on low fertile land (sea < alt < highland). */
    FOOD_LOW_LAND: 0.15,
    /** Health gained per tick on highland (highland < alt < mountain). Must stay
     *  *above* `HEALTH_DECAY_BASE`: at exactly 0.04 the gain and the decay cancel to
     *  a net zero each tick, so a Being on highland never reaches `FULL_THRESHOLD`
     *  and is trapped in the Eating state forever. At 0.06 it nets +0.02/tick and
     *  climbs to full in ~33 ticks — against +0.11/tick and ~7 ticks on low land, so
     *  highland still reads as thin grazing the herd survives on rather than thrives
     *  on, which is the tension Level 2's hunting ground depends on. */
    FOOD_HIGHLAND: 0.06,
};

export enum Direction {
    North,
    Northeast,
    East,
    Southeast,
    South,
    Southwest,
    West,
    Northwest
}

export const Directions = [
    Direction.North,
    Direction.Northeast,
    Direction.East,
    Direction.Southeast,
    Direction.South,
    Direction.Southwest,
    Direction.West,
    Direction.Northwest
];

export const DirectionNames: Map<Direction, string> = new Map([
    [Direction.North, "North"],
    [Direction.Northeast, "Northeast"],
    [Direction.East, "East"],
    [Direction.Southeast, "Southeast"],
    [Direction.South, "South"],
    [Direction.Southwest, "Southwest"],
    [Direction.West, "West"],
    [Direction.Northwest, "Northwest"]
]);

const NorthZ = -1;
const SouthZ = 1;
const EastX = 1;
const WestX = -1;

export const DirectionsVectors: Map<Direction, { dx: number; dz: number }> = new Map([
    [Direction.North, { dx: 0, dz: NorthZ }],
    [Direction.Northeast, { dx: EastX, dz: NorthZ }],
    [Direction.East, { dx: EastX, dz: 0 }],
    [Direction.Southeast, { dx: EastX, dz: SouthZ }],
    [Direction.South, { dx: 0, dz: SouthZ }],
    [Direction.Southwest, { dx: WestX, dz: SouthZ }],
    [Direction.West, { dx: WestX, dz: 0 }],
    [Direction.Northwest, { dx: WestX, dz: NorthZ }]
]);